import React, { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Button } from "@material-ui/core";
import { MoreVert, Replay, FilterList } from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

import TicketFunnelModal from "../TicketFunnelModal";
import OpportunityModal from "../OpportunityModal";
import OpportunityHistoryModal from "../OpportunityHistoryModal";
import TemperatureModal from "../TemperatureModal";
import { showOppByContact, moveOpportunity } from "../../services/funnelOpportunities";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
	funnelBtn: {
		backgroundColor: "#7B1FA2", // Roxo padrão
		color: "#fff",
		"&:hover": {
			backgroundColor: "#4A148C",
		},
	},
	// Hide individual buttons on mobile, show only menu
	hideOnMobile: {
		[theme.breakpoints.down("sm")]: {
			display: "none !important",
		},
	},
	// Show only on mobile
	showOnMobile: {
		display: "none !important",
		[theme.breakpoints.down("sm")]: {
			display: "inline-flex !important",
		},
	},
}));

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	// Kanban Funnel States
	const [funnelModalOpen, setFunnelModalOpen] = useState(false);
	const [opportunityModalOpen, setOpportunityModalOpen] = useState(false);
	const [temperatureModalOpen, setTemperatureModalOpen] = useState(false);
	const [historyModalOpen, setHistoryModalOpen] = useState(false);
	const [opportunity, setOpportunity] = useState(null);
	const [newOpportunityData, setNewOpportunityData] = useState(null); // Para passar dados ao TemperatureModal

	useEffect(() => {
		if (ticket?.contact?.id) {
			loadOpportunity();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticket]);

	const loadOpportunity = async () => {
		try {
			const { data } = await showOppByContact(ticket.contact.id);
			setOpportunity(data);
		} catch (err) {
			// Silently fail if no opportunity found or error
			console.log("No active opportunity found or error fetching:", err);
		}
	};

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};


	const handleFunnelButtonClick = () => {
		if (opportunity) {
			setOpportunityModalOpen(true);
		} else {
			setFunnelModalOpen(true);
		}
	};

	const handleOpportunityCreated = (data) => {
		setOpportunity(data);
		setNewOpportunityData(data); // Salva dados da nova oportunidade
		setTimeout(() => {
			setTemperatureModalOpen(true); // Abre modal de temperatura
		}, 500);
	};

	const handleCardChanged = (data) => {
		console.log("[Funnel] Card changed, opening temperature modal:", data);
		setNewOpportunityData(data); // Salva dados da mudança de card
		setTimeout(() => {
			setTemperatureModalOpen(true); // Abre modal de temperatura
		}, 500);
	};

	const handleTemperatureConfirm = async ({ temperature, notes }) => {
		try {
			console.log("[Funnel] Confirming temperature with data:", {
				opportunityId: newOpportunityData.opportunityId || newOpportunityData.id,
				funnelCardId: newOpportunityData.funnelCardId,
				temperature,
				notes
			});
			await moveOpportunity(
				newOpportunityData.opportunityId || newOpportunityData.id,
				newOpportunityData.funnelCardId,
				temperature,
				notes
			);
			setTemperatureModalOpen(false);
			loadOpportunity(); // Recarrega para garantir dados atualizados
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<>
			<div className={classes.actionButtons}>
				{ticket.status === "closed" && (
					<button
						className="btn btn-white text-primary btn-shadow btn-icon waves-effect me-2"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
						title={i18n.t("messagesList.header.buttons.reopen")}
					>
						<i className="fi fi-rr-refresh"></i>
					</button>
				)}
				{ticket.status === "open" && (
					<>
						<button
							className={`btn btn-white text-info btn-shadow btn-icon waves-effect me-2 ${classes.hideOnMobile}`}
							onClick={e => handleUpdateTicketStatus(e, "pending", null)}
							title={i18n.t("messagesList.header.buttons.return")}
						>
							<i className="fi fi-rr-undo"></i>
						</button>

						{/* Qualificação de Leads (Kanban) */}
						<button
							className={`btn btn-white btn-shadow waves-effect me-2 ${opportunity?.card ? 'px-3' : 'btn-icon'} ${classes.hideOnMobile}`}
							style={opportunity?.card ? { color: opportunity.card.color || "#7B1FA2" } : { color: "#7B1FA2" }}
							onClick={handleFunnelButtonClick}
							title={opportunity?.card ? opportunity.card.name : "Qualificar Lead"}
						>
							<i className="fi fi-rr-filter"></i>
							{opportunity?.card && (
								<span className="ms-2 fw-semibold">{opportunity.card.name}</span>
							)}
						</button>

						{/* Botão Resolver */}
						<button
							className={`btn btn-white text-success btn-shadow btn-icon waves-effect me-2 ${classes.hideOnMobile}`}
							onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
							title={i18n.t("messagesList.header.buttons.resolve")}
						>
							<i className="fi fi-rr-check"></i>
						</button>

						{/* Opções (Três Pontos) - Desktop */}
						<button
							className={`btn btn-white text-dark btn-shadow btn-icon waves-effect ${classes.hideOnMobile}`}
							onClick={handleOpenTicketOptionsMenu}
						>
							<i className="fi fi-rr-menu-dots-vertical"></i>
						</button>

						{/* Opções (Três Pontos) - Mobile (TODAS as opções) */}
						<button
							className={`btn btn-white text-dark btn-shadow btn-icon waves-effect ${classes.showOnMobile}`}
							onClick={handleOpenTicketOptionsMenu}
							title="Opções"
						>
							<i className="fi fi-rr-menu-dots-vertical"></i>
						</button>

						<TicketOptionsMenu
							ticket={ticket}
							anchorEl={anchorEl}
							menuOpen={ticketOptionsMenuOpen}
							handleClose={handleCloseTicketOptionsMenu}
							handleOpenHistory={() => {
								setHistoryModalOpen(true);
							}}
							// Passar funções para mobile
							onReturn={() => handleUpdateTicketStatus(null, "pending", null)}
							onResolve={() => handleUpdateTicketStatus(null, "closed", user?.id)}
							onFunnel={handleFunnelButtonClick}
						/>
					</>
				)}
				{ticket.status === "pending" && (
					<button
						className="btn btn-success btn-shadow px-3 waves-effect"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</button>
				)}
			</div>

			<TicketFunnelModal
				open={funnelModalOpen}
				onClose={() => setFunnelModalOpen(false)}
				ticket={ticket}
				onOpportunityCreated={handleOpportunityCreated}
			/>

			{opportunity && (
				<OpportunityModal
					open={opportunityModalOpen}
					onClose={() => {
						setOpportunityModalOpen(false);
						loadOpportunity(); // Reload to get updated card info
					}}
					funnelId={opportunity.funnelId}
					opportunityId={opportunity.id}
					reload={loadOpportunity}
					onCardChanged={handleCardChanged}
				/>
			)}

			<OpportunityHistoryModal
				open={historyModalOpen}
				onClose={() => setHistoryModalOpen(false)}
				opportunity={opportunity}
				ticket={ticket}
			/>

			<TemperatureModal
				open={temperatureModalOpen}
				onClose={() => setTemperatureModalOpen(false)}
				opportunityTitle={newOpportunityData?.title}
				fromCard={null}
				toCard={newOpportunityData?.card || { name: "Estágio Inicial" }}
				onConfirm={handleTemperatureConfirm}
			/>
		</>
	);
};

export default TicketActionButtons;
